import React from 'react';
import moment from 'moment';
import { FullStoryAPI } from 'react-fullstory';
import SessionStore from 'stores/shared/session';
import invariant from 'fbjs/lib/invariant';
import cx from 'classnames';
import _ from 'underscore';
import I from 'immutable';

import 'views/support/_transitionable.scss';

const Transitionable = {
  transitionableStoreInterests() {
    return {
      screensStackTop: 'navigation.screensStackLast',
    };
  },

  transitionableInitialState() {
    return {
      activeScreen: I.fromJS(this.props.initialScreen),
      screensStackTop: I.fromJS(this.props.initialScreen),
      transitionStarted: false,
      transitionDirection: null,
      isTransitioning: false,
    };
  },

  getTransitionTime(type) {
    const ms = this.screenTransitionTime || 1000;
    switch (type) {
      case 'css':
        return `${ms / 1000}s`;
        break;
      default:
        return ms;
        break;
    }
  },

  initiateTransition() {
    // Set the state so that both screens
    // will be rendered.
    this.setState({
      transitionStarted: true,
      transitionDirection:
        this.state.activeScreen.get('stackIndex') >
        this.state.screensStackTop.get('stackIndex')
          ? 'back'
          : 'fwd',
    });

    // Schedule a cleanup so that we remove the
    // previous screen after the transition occurs
    // An extra 150ms for smoothness
    setTimeout(() => this.finalizeTransition(), this.getTransitionTime() + 150);
  },

  finalizeTransition() {
    this.setState({
      activeScreen: this.state.screensStackTop,
      transitionStarted: false,
      isTransitioning: false,
      transitionDirection: null,
    });
  },

  shouldTransition() {
    return (
      !this.state.transitionStarted &&
      !this._equalScreens(this.state.activeScreen, this.state.screensStackTop)
    );
  },

  shouldStartAnimation() {
    return this.state.transitionStarted && !this.state.isTransitioning;
  },

  componentDidUpdate(prevProps, prevState) {
    if (this.shouldTransition()) {
      this.initiateTransition();
    } else if (this.shouldStartAnimation()) {
      this.setState({ isTransitioning: true });
    }
  },

  _equalScreens(screenA, screenB) {
    if (screenA.get('component') !== screenB.get('component')) {
      return false;
    }

    if (
      screenA.get('props') !== undefined &&
      screenB.get('props') !== undefined
    ) {
      return (
        screenA.get('props').hashCode() === screenB.get('props').hashCode()
      );
    }
    return screenA.get('props') === screenB.get('props');
  },

  renderScreen(screenDescriptor, options = {}) {
    let Screen,
      screenProps,
      stackIndex,
      classes,
      scrollAdjust = {},
      style;

    Screen = eval(screenDescriptor.get('component'));
    screenProps = screenDescriptor.get('props');
    stackIndex = screenDescriptor.get('stackIndex');

    invariant(
      typeof Screen !== 'undefined',
      `Cannot find screen "${screenDescriptor.get('component')}"`
    );

    if (typeof screenProps.toJS === 'function') {
      screenProps = screenProps.toJS();
    }

    if (typeof this.getScreenProps === 'function') {
      screenProps = this.getScreenProps(
        screenDescriptor.get('component'),
        screenProps
      );
    }

    style = {
      transitionDuration: this.getTransitionTime('css'),
      MozTransitionDuration: this.getTransitionTime('css'),
      WebkitTransitionDuration: this.getTransitionTime('css'),
      msTransitionDuration: this.getTransitionTime('css'),
    };

    classes = cx({
      transitioning: this.state.isTransitioning,
      'transitioning-to': options.isNext,
      'transitioning-from': options.isPrevious,
      'transitioning-left': this.state.transitionDirection == 'fwd',
      'transitioning-right': this.state.transitionDirection == 'back',
    });

    if (options.isPrevious) {
      scrollAdjust = {
        top: `${-document.body.scrollTop}px`,
        height: document.body.scrollTop + window.innerHeight,
      };
    } else if (options.isNext) {
      scrollAdjust = {
        height: window.innerHeight,
      };
    }
    style = _.extend(style, scrollAdjust);

    return (
      <div
        className={`transitionable ${classes}`}
        style={style}
        key={`screen${stackIndex}`}
      >
        <Screen {...screenProps} />
      </div>
    );
  },

  renderTransitionableScreens() {
    const uuid = SessionStore.state.get('uuid');
    const bookingInitiatedDateUTC = moment.utc().format('YYYY-MM-DD');
    const bookingInitiatedTimeUTC = moment.utc().format('LT');
    FullStoryAPI('identify', uuid, {
      bookingInitiatedDateUTC,
      bookingInitiatedTimeUTC,
    });

    if (this.state.transitionStarted) {
      return [
        this.renderScreen(this.state.activeScreen, { isPrevious: true }),
        this.renderScreen(this.state.screensStackTop, { isNext: true }),
      ];
    }
    return [this.renderScreen(this.state.activeScreen)];
  },
};

export default Transitionable;
